<template>
    <div class="statistics-wrapper">
        <div class="statistics-layout">
            <SideBar 
                :open="filtersOpen"
                title="Filtros"
                @apply-filters="ApplySelected"
                @clear-filters="ClearSelected"
            >
                <template #title>
                    <p style="margin:0;">FILTROS</p>
                </template>
                <template #content>
                    <ModulesModalitiesAndSchedulesSelector
                        :modules="modules"
                        :modalities="modalities"
                        :schedules="schedules"
                        :value="modules_and_modalities"
                        @module-change="ModuleChangeHandler"
                        @modality-change="ModalityChangeHandler"
                        @schedule-change="ScheduleChangeHandler"
                        @select-all="HandleSelectAll"
                        @toggle-expand="ToggleExpand"
                    />
                </template>
            </SideBar>
            <div :class="['statistics-content', { 'content-shrinked': filtersOpen }]">
                <div class="title">
                    <p style="margin: 0;">ESTATÍSTICAS</p>   
                </div>
                <div class="subtitle subtitle-flex">
                    <div class="subtitle-btn-wrapper">
                        <button
                            class="filter-btn"
                            :class="{ 'filter-btn--active': hasActiveFilters }"
                            @click="ToggleFilters"
                        >
                            <i class="fa fa-filter"></i>
                            <span>FILTROS</span>
                        </button>
                    </div>
                    <div class="subtitle-btn-right">
                        <button 
                            class="btn btn-light-refresh btn-sm"
                            :disabled="refreshing"
                            @click="HandleRefresh"
                            style="position: relative; min-width: 2.2rem;"
                        >
                            <i class="far fa-sync"></i>
                            <span v-if="refreshing" class="spinner" style="position: absolute; right: 3.5rem; top: 50%; transform: translateY(-50%);">
                                <i class="fas fa-circle-notch fa-spin"></i>
                            </span>
                        </button>
                        <button
                            class="btn btn-light-refresh btn-sm"
                            @click="FullScreenHandler"
                            style="min-width: 2.2rem;"
                        >
                            <i class="far fa-expand-wide"></i>
                        </button>
                    </div>
                </div>
                <GraphContainer
                    ref="graphContainer"
                    :modalities="selectedModalities"
                    :schedules="selectedSchedules"
                />
            </div>
        </div>
        <div :class="['footer', { 'footer-shrinked': filtersOpen }]"></div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import SideBar from '../shared/SideBar.vue';
import ModulesModalitiesAndSchedulesSelector from './components/ModulesModalitiesAndSchedulesSelector.vue';
import GraphContainer from './GraphContainer.vue';
import Swal from 'sweetalert2';

export default {
    name: 'Statistics',
    components: {
        SideBar,
        ModulesModalitiesAndSchedulesSelector,
        GraphContainer
    },
    data() {
        return {
            filtersOpen: false,
            modules_and_modalities: {
                select_all: false
            },
            selectedModalities: [],
            selectedSchedules: [],
            refreshing: false
        }
    },
    computed: {
        ...mapGetters('system', [
            'modules', 'modalities', 'schedules'
        ]),
        ...mapGetters('auth', [
            'requester_selected_id', 'account', 'requesters'
        ]),
        hasActiveFilters() {
            for (const module of this.modules) {
                const modObj = this.modules_and_modalities[`module_${module.id}`];
                if (modObj?.selected) return true;
            }

            for (const modality of this.modalities) {
                const modObj = this.modules_and_modalities[`modality_${modality.id}`];
                if (modObj?.schedules?.length) return true;
            }
            return false;
        }
    },
    methods: {
        ...mapActions('system', [
            'StartLoading', 'FinishLoading', 'GetModulesModalitiesAndSchedules'
        ]),
        ToggleFilters() {
            this.filtersOpen = !this.filtersOpen;

            this.SaveStatisticsPreferences();
        },
        HandleSelectAll(val) {
            this.modules.forEach(module => {
                const moduleKey = `module_${module.id}`;
                this.modules_and_modalities[moduleKey].selected = val;

                const modalitiesIds = this.modalities
                    .filter(m => m.module_id === module.id)
                    .map(m => m.id);
                this.modules_and_modalities[moduleKey].modalities = val ? modalitiesIds : [];

                modalitiesIds.forEach(modality_id => {
                    const modalityKey = `modality_${modality_id}`;
                    const schedulesIds = this.schedules
                        .filter(s => s.modality_id === modality_id)
                        .map(s => s.id);
                    this.modules_and_modalities[modalityKey].schedules = val ? schedulesIds : [];
                });
            });

            this.modules_and_modalities.select_all = val;
        },
        CheckSelectAll() {
            const allModulesSelected = this.modules.every(module => {
                const modObj = this.modules_and_modalities[`module_${module.id}`];

                return modObj && modObj.selected;
            });

            const allModalitiesSelected = this.modalities.every(modality => {
                const moduleObj = this.modules_and_modalities[`module_${modality.module_id}`];

                return moduleObj && moduleObj.modalities.includes(modality.id);
            });

            const allSchedulesSelected = this.modalities.every(modality => {
                const modalityObj = this.modules_and_modalities[`modality_${modality.id}`];
                const scheduleIds = this.schedules.filter(s => s.modality_id === modality.id).map(s => s.id);
                
                return (
                    modalityObj &&
                    scheduleIds.every(id => modalityObj.schedules.includes(id))
                );
            });

            this.modules_and_modalities.select_all = allModulesSelected && allModalitiesSelected && allSchedulesSelected;
        },
        ModuleChangeHandler(event, module_id) {
            const checked = event.target.checked;
            const moduleObj = this.modules_and_modalities[`module_${module_id}`];

            const modalitiesIds = this.modalities
                .filter((m) => m.module_id === module_id)
                .map((m) => m.id);

            moduleObj.selected = checked;
            moduleObj.modalities = checked ? modalitiesIds : [];

            modalitiesIds.forEach(modality_id => {
                if(!this.modules_and_modalities[`modality_${modality_id}`]) {
                    this.$set(this.modules_and_modalities, `modality_${modality_id}`, { expanded: false, schedules: [] });
                }

                const schedulesIds = this.schedules
                    .filter((s) => s.modality_id === modality_id)
                    .map((s) => s.id);

                this.modules_and_modalities[`modality_${modality_id}`].schedules = checked ? schedulesIds : [];
            });

            if(!checked && this.modules_and_modalities.select_all) {
                this.modules_and_modalities.select_all = false;
            }

            this.CheckSelectAll();
        },
        ModalityChangeHandler(event, module_id, modality_id) {
            const checked = event.target.checked;
            const modObj = this.modules_and_modalities[`module_${module_id}`];

            if(checked) {
                modObj.selected = true;

                if(!modObj.modalities.includes(modality_id)) {
                    modObj.modalities.push(modality_id);
                }
            } else {
                modObj.modalities = modObj.modalities.filter(id => id !== modality_id);

                if(modObj.modalities.length === 0) {
                    modObj.selected = false;
                }
            }

            if(!this.modules_and_modalities[`modality_${modality_id}`]) {
                this.$set(this.modules_and_modalities, `modality_${modality_id}`, { expanded: false, schedules: [] });
            }

            const schedulesIds = this.schedules
                .filter((s) => s.modality_id === modality_id)
                .map((s) => s.id);

            this.modules_and_modalities[`modality_${modality_id}`].schedules = checked ? schedulesIds : [];

            if(!checked && this.modules_and_modalities.select_all) {
                this.modules_and_modalities.select_all = false;
            }

            this.CheckSelectAll();
        },
        ScheduleChangeHandler(event, module_id, modality_id, schedule_id) {
            const checked = event.target.checked;
            const modalityObj = this.modules_and_modalities[`modality_${modality_id}`];
            const moduleObj = this.modules_and_modalities[`module_${module_id}`];

            if(!modalityObj) return;

            if(checked) {
                if (!modalityObj.schedules.includes(schedule_id)) {
                    modalityObj.schedules.push(schedule_id);
                }

                if (moduleObj && !moduleObj.modalities.includes(modality_id)) {
                    moduleObj.modalities.push(modality_id);
                    moduleObj.selected = true;
                }
            } else {
                modalityObj.schedules = modalityObj.schedules.filter(id => id !== schedule_id);

                if(moduleObj) {
                    const hasOtherSchedules = modalityObj.schedules.length > 0;
                    if(!hasOtherSchedules) {
                        moduleObj.modalities = moduleObj.modalities.filter(id => id !== modality_id);
                        if(moduleObj.modalities.length === 0) {
                            moduleObj.selected = false;
                        }
                    }
                }
            }

            if(!checked && this.modules_and_modalities.select_all) {
                this.modules_and_modalities.select_all = false;
            }

            this.CheckSelectAll();
        },
        ToggleExpand(type, id) {
            const key = `${type}_${id}`;

            if(!this.modules_and_modalities[key]) {
                this.$set(this.modules_and_modalities, key, { 
                    expanded: true, 
                    ...(type === 'modality' ? { schedules: [] } : {}) 
                });
            } else {
                this.modules_and_modalities[key].expanded = !this.modules_and_modalities[key].expanded;
            }
        },
        GetSelectedModulesAndModalities() {
            let modalities = [];
            let schedules = [];

            this.modules.forEach(module => {
                const modObj = this.modules_and_modalities[`module_${module.id}`];

                if(modObj?.selected) {
                    modObj.modalities.forEach(modality_id => {
                        if(!modalities.includes(modality_id)) {
                            modalities.push(modality_id);
                        }

                        const modalityObj = this.modules_and_modalities[`modality_${modality_id}`];
                        if(modalityObj?.schedules) {
                            modalityObj.schedules.forEach(schedule_id => {
                                if(!schedules.includes(schedule_id)) {
                                    schedules.push(schedule_id);
                                }
                            });
                        }
                    });
                }
            });

            this.modalities.forEach(modality => {
                const modalityObj = this.modules_and_modalities[`modality_${modality.id}`];
                const moduleObj = this.modules_and_modalities[`module_${modality.module_id}`];
                const isModalitySelected = moduleObj?.modalities?.includes(modality.id);

                if(modalityObj && isModalitySelected) {
                    return;
                }

                if(modalityObj?.schedules?.length) {
                    if(!modalities.includes(modality.id)) {
                        modalities.push(modality.id);
                    }
                    modalityObj.schedules.forEach(schedule_id => {
                        if(!schedules.includes(schedule_id)) {
                            schedules.push(schedule_id);
                        }
                    });
                }
            });

            this.modalities.forEach(modality => {
                const modalityObj = this.modules_and_modalities[`modality_${modality.id}`];

                if(modalityObj?.schedules?.length && !modalities.includes(modality.id)) {
                    modalityObj.schedules.forEach(schedule_id => {
                        if(!schedules.includes(schedule_id)) {
                            schedules.push(schedule_id);
                        }
                    });
                }
            });

            return {
                modalities,
                schedules
            };
        },
        async ApplySelected() {
            const { modalities, schedules } = this.GetSelectedModulesAndModalities();

            this.selectedModalities = modalities;
            this.selectedSchedules = schedules;

            await this.SaveStatisticsPreferences();
        },
        ClearSelected() {
            this.modules.forEach(module => {
                const modKey = `module_${module.id}`;

                if(this.modules_and_modalities[modKey]) {
                    this.modules_and_modalities[modKey].selected = false;
                    this.modules_and_modalities[modKey].modalities = [];
                }
            });

            this.modalities.forEach(modality => {
                const modKey = `modality_${modality.id}`;

                if(this.modules_and_modalities[modKey]) {
                    this.modules_and_modalities[modKey].schedules = [];
                }
            });

            this.modules_and_modalities.select_all = false;
        },
        async SaveStatisticsPreferences() {
            const payload = {
                organization_id: this.requester_selected_id,
                account_provider_id: this.account.id,
                filters: {
                    filtersOpen: this.filtersOpen,
                    modules_and_modalities: this.modules_and_modalities
                }
            }

            return window.api.call('post', '/save-statistics-preferences', payload)
                .then(({status, data}) => {
                    if(status === 200) {
                        return;
                    }

                    Swal.fire({
                        icon: 'error',
                        title: 'Erro!',
                        text: 'Ocorreu um erro ao salvar as preferências de estatísticas!'
                    });
                });
        },
        async GetStatisticsPreferences() {
            return window.api.call('post', '/get-statistics-preferences', {
                account_provider_id: this.account.id,
                organization_id: this.requester_selected_id
            })
                .then(({status, data}) => {
                    if(status === 200) {
                        if(data.filters) {
                            this.filtersOpen = data.filters.filtersOpen || false;
                            this.modules_and_modalities = data.filters.modules_and_modalities || { select_all: false };

                            const { modalities, schedules } = this.GetSelectedModulesAndModalities();

                            this.selectedModalities = modalities;
                            this.selectedSchedules = schedules;
                        }

                        return;
                    }

                    Swal.fire({
                        icon: 'error',
                        title: 'Erro!',
                        text: 'Ocorreu um erro ao buscar as preferências de estatísticas!'
                    });
                });
        },
        async HandleRefresh() {
            if(this.refreshing) return

            this.refreshing = true

            try {
                if(this.$refs.graphContainer && this.$refs.graphContainer.GetStatistics) {
                    await this.$refs.graphContainer.GetStatistics(true)
                }
            } finally {
                this.refreshing = false
            }
        },
        FullScreenHandler() {
            const container = document.querySelector('#statistics-container')
            if (!container) return

            if (container.requestFullscreen) {
                container.requestFullscreen()
            } else if (container.webkitRequestFullscreen) {
                container.webkitRequestFullscreen()
            } else if (container.msRequestFullscreen) {
                container.msRequestFullscreen()
            }
        },
    },
    async mounted() {
        await this.GetModulesModalitiesAndSchedules();

        this.modules_and_modalities = { select_all: false };

        this.modules.forEach(module => {
            this.$set(this.modules_and_modalities, `module_${module.id}`, {
                id: module.id,
                selected: false,
                expanded: false,
                modalities: []
            });
        });

        this.modalities.forEach(modality => {
            this.$set(this.modules_and_modalities, `modality_${modality.id}`, {
                expanded: false,
                schedules: []
            });
        });

        await this.GetStatisticsPreferences();
    }
}
</script>

<style lang="scss" scoped>
.statistics-wrapper {
    width: 100%;
    height: 100%;
    position: fixed;
    left: 0;
    background: var(--graphBackgroundColor);
    
    .statistics-layout {
        display: flex;
        width: 100vw;
        height: calc(100vh - 6.235rem);
    }

    .statistics-content {
        flex: 1 1 75vw;
        width: 75vw;
        min-width: 0;
        height: 100%;
        display: flex;
        flex-direction: column;
        background-color: var(--graphBackgroundColor);
        overflow-y: auto;
        padding-bottom: 4rem;
        transition: width 0.3s;
    }

    .content-shrinked {
        width: 75vw;
    }

    .footer {
        position: fixed;
        bottom: 0;
        left: 0;
        background-color: var(--barsBackground);
        color: var(--graphBackgroundColor);
        padding: 0.375rem 0.938rem;
        text-align: left;
        width: 100%;
        height: 3.75rem;
        z-index: 10;

        &.footer-shrinked {
            width: 75vw;
            left: 25vw;
        }
    }

    .menu-btn {
        background: var(--barsBackground);
        border: none;
        cursor: pointer;
        padding: 0px;
    }

    .side-menu-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: var(--barsBackground);
        z-index: 100;
        display: flex;
        justify-content: flex-end;
    }

    .title {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        height: 3.125rem;
        background: var(--barsBackground);
        font-size: 22px;
        color: var(--titleFontColor);
        font-weight: bold;
        border-bottom: 1px solid var(--separatorColor);
    }

    .subtitle {
        display: flex;
        flex-direction: row;
        align-items: center;
        height: 3.125rem;
        background: var(--barsBackground);
        font-size: 18px;
        color: var(--titleFontColor);
        position: relative;
        padding: 0 1rem;
    }

    .subtitle-flex {
        justify-content: space-between;
    }

    .subtitle-btn-wrapper {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
    }

    .subtitle-btn-right {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        gap: 1rem;
    }

    .subtitle-center-text {
        flex: 1 1 0;
        text-align: center;
        font-size: 18px;
        font-weight: 500;
        letter-spacing: 0.5px;
    }

    .subtitle-btn-placeholder {
        flex: 0 0 90px;
    }

    .filter-btn {
        background: #143445;
        box-shadow: 0px 0px 15px 10px #184257 inset;
        border: none;
        border-radius: 4px;
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 2.2rem;
        min-width: 2.2rem;
        padding: 0 10px;
        margin-right: 12px;
        font-weight: 600;
        font-size: 1rem;
        cursor: pointer;
        transition: background 0.2s;
        font-family: inherit;

        &:hover {
            box-shadow: 0px 0px 15px 1px #18324a;
            background: #18324a;
        }

        i {
            color: #fff;
            font-size: 0.85rem;
        }

        span {
            color: #fff;
            font-size: 0.85rem;
            margin-left: 6px;
            font-weight: 600;
        }
    }

    .filter-btn--active {
        background: #a3a121 !important;
        color: #fff !important;
        text-shadow: 0px 0px 7px #fff;
        box-shadow: none;

        &:hover {
            background: #6d6b18 !important;
        }
    }

    .btn-light-refresh,
    .btn-light-refresh.disabled,
    .btn-light-refresh:disabled{
        color: #fff;
        background-color: var(--buttonPrimary);
        border-color: var(--buttonPrimary);
        filter: grayscale(0.4);
    }

    .btn-light-refresh:hover,
    a.btn-light-refresh:hover,
    .btn-light-refresh:not(:disabled):not(.disabled).active,
    .btn-light-refresh:not(:disabled):not(.disabled):active,
    .btn-light-refresh.focus,
    .btn-light-refresh:focus {
        color: #fff !important;
        background-color: var(--buttonPrimaryHover);
        border-color: var(--buttonPrimaryHover);
    }

    .btn-sm {
        padding: 0.45rem 1rem !important;
    }

    .spinner {
        color: var(--primary);
    }
}
</style>